import React, { useContext, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { PopupDialog } from '@eposnow/ui-core';
import { Box, Button, Card, CircularProgress, LinearProgress, Typography } from '@mui/material';
import { API_V1_HOSTNAME_KEY } from '../../localStorageKeys';
import { HOPUrls, KYCResponseType } from '../../types';
import { ApiContext, ErrorObject } from '../../context/ApiContext';
import { UIContext } from '../../context/UIContext';
import ActionsContainer from '../../components/ActionsContainer';
import { StepContext } from '../../context/StepContext';
import { ReactComponent as Hourglass } from '../../img/icons/hourglass.svg';
import { ReactComponent as HourglassWhite } from '../../img/icons/hourglass-white.svg';
import { ReactComponent as SuccessIcon } from '../../img/success.svg';
import { ReactComponent as SuccessIconWhite } from '../../img/success-white.svg';
import { ReactComponent as WarningIcon } from '../../img/icons/warning.svg';
import { ReactComponent as WarningIconWhite } from '../../img/icons/warning-white.svg';
import Loader from '../../components/Loader';
import { isKycPassed } from './helpers/helpers';
import PageHeader from '../../components/pageHeaderComponents/PageHeader';
import useBusinessInformation from '../../helperHooks/useBusinessInformation';
import SkipStepPopup from '../../components/SkipStepPopup';
import usePageTitle from '../../helperHooks/usePageTitle';

let timeout: NodeJS.Timeout;

const Verification = () => {
  const { t } = useTranslation();
  const { apiFetch } = useContext(ApiContext);
  const { colorMode, isMobile, isTablet } = useContext(UIContext);
  const { goToPreviousStep, goToNextStep } = useContext(StepContext);
  const { businessInformation, isLoading: isLoadingBusinessInfo } = useBusinessInformation();
  const [hopRetries, setHopRetries] = useState(1);
  const [loadingHop, setLoadingHop] = useState(false);
  const [isLoadingFirstKycStatus, setIsLoadingFirstKycStatus] = useState(true); // First KYC data load, page shows loading
  const [isLoadingKycStatus, setIsLoadingKycStatus] = useState(false); // "Refresh" button KYC data load, only button shows loading
  const [hopFetchEnabled, setHopFetchEnabled] = useState(false);
  const [isKycStarted, setIsKycStarted] = useState(false); // True if client has started the Adyen process already
  const [skipStepModalOpen, setSkipStepModalOpen] = useState<boolean>(false);
  const [errorCreatingEntity, setErrorCreatingEntity] = useState(false);
  usePageTitle('nav.verification');

  const resetHopLogic = () => {
    setHopRetries(1);
    setLoadingHop(false);
    setHopFetchEnabled(false);
  };

  useEffect(
    () => () => {
      clearTimeout(timeout);
      timeout = undefined;
    },
    []
  );

  const { refetch: refetchKycStatus, data: kycStatus } = useQuery<KYCResponseType>(
    ['kyc'],
    () => apiFetch(`${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/get-capabilities`),
    {
      onSuccess(response) {
        // If status is 204 it means that the legal entity wasn't created yet, and so not possible to have started yet the KYC process
        if (response?.status === 204) {
          setIsKycStarted(false);
        } else {
          setIsKycStarted(true);
        }

        setIsLoadingKycStatus(false);
        setIsLoadingFirstKycStatus(false);
      },
      onError(err: ErrorObject) {
        setIsLoadingKycStatus(false);
        setIsLoadingFirstKycStatus(false);

        if (err && err?.code === 401) return;

        Sentry.captureException(new Error('Error fetching HOP urls'), {
          extra: { error: JSON.stringify(err) },
        });
      },
    }
  );

  const { refetch: refetchHop, isLoading } = useQuery<HOPUrls>(
    ['hopUrls'],
    () =>
      apiFetch(
        `${localStorage.getItem(
          API_V1_HOSTNAME_KEY
        )}onboarding/proxy/get-onboarding-link?returnUrl=${window.location.href}`
      ),
    {
      enabled: hopFetchEnabled,
      onSuccess(data: HOPUrls) {
        // If the Adyen link is returned, reset attempt counter and redirect to Adyen page
        resetHopLogic();
        window.location.href = data.Url;
      },
      onError(err: ErrorObject) {
        if (err && err?.code === 401) return;

        // If the call fails it might be because the client entity is still being created
        // In this case, we set up a maximum number of retries, set a timeout and poll this endpoint until it either succeeds or runs out of attempts
        if (hopRetries < 4) {
          setHopRetries((prevRetries) => prevRetries + 1);
          clearTimeout(timeout);
          timeout = setTimeout(refetchHop, Math.min(5000 * 2 ** hopRetries, 30000));
        } else {
          Sentry.captureException(new Error('Error fetching HOP urls'), {
            extra: { error: JSON.stringify(err) },
          });
          resetHopLogic();
          setErrorCreatingEntity(true);
        }
      },
    }
  );

  const loadAndOpenHOP = () => {
    setHopRetries(1);
    setLoadingHop(true);
    setHopFetchEnabled(true);
    refetchHop();
  };

  return (
    <>
      <PageHeader />

      <SkipStepPopup
        open={skipStepModalOpen}
        popupTitle={t('screens.verification.skipStep.title')}
        popupDescription={t('screens.verification.skipStep.description')}
        handleClose={() => setSkipStepModalOpen(false)}
        handleSkip={goToNextStep}
      />

      <PopupDialog
        open={hopRetries > 1 && loadingHop}
        icon={colorMode === 'light' ? <Hourglass /> : <HourglassWhite />}
        title={t('screens.verification.verificationNotComplete.popupTitle')}
        headerTextStyles={{ fontSize: '24px', fontWeight: 400 }}
        description={
          <Box sx={{ width: '100%' }}>
            <Box sx={{ marginBottom: '2rem' }}>
              <Typography variant="body1" sx={{ marginBottom: '0.5rem' }}>
                {t('screens.verification.verificationNotComplete.popupDescription1')}
              </Typography>
              <Typography variant="body1">
                {t('screens.verification.verificationNotComplete.popupDescription2')}
              </Typography>
            </Box>
            <LinearProgress />
          </Box>
        }
        hideCloseButton
        dialogSx={{
          maxWidth: isMobile ? '100%' : '50%',
        }}
      />
      <Card
        variant="outlined"
        sx={{
          padding: isMobile ? '2rem' : '3rem',
          marginBottom: '3rem',
          ...((isMobile || isTablet) && {
            marginBottom: '5rem',
          }),
        }}
      >
        {(isLoadingFirstKycStatus || isLoadingBusinessInfo) && (
          <Loader ariaBusy={isLoadingFirstKycStatus || isLoadingBusinessInfo} />
        )}

        {/* Legal entity couldn't be created, show warning and let client proceed (they can Verify their account later) */}
        {!(isLoadingFirstKycStatus || isLoadingBusinessInfo) && errorCreatingEntity && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              flexGrow: 1,
              textAlign: 'center',
              alignItems: 'center',
              gap: '1rem',
              padding: '2rem',
            }}
          >
            {colorMode === 'light' ? <WarningIcon /> : <WarningIconWhite />}
            <Typography variant="h1" sx={{ marginTop: '0.5rem' }}>
              {t('screens.verification.errorCreatingEntity.title')}
            </Typography>
            <Typography variant="body1">
              {t('screens.verification.errorCreatingEntity.description')}
            </Typography>
          </Box>
        )}

        {/* No longer loading, the entity was created */}
        {!(isLoadingFirstKycStatus || isLoadingBusinessInfo) &&
          !errorCreatingEntity &&
          (isKycPassed(kycStatus, businessInformation?.issueBankAccount) ? (
            // Kyc has been successfuly finished, client can proceed
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                flexGrow: 1,
                textAlign: 'center',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              {colorMode === 'light' ? <SuccessIcon /> : <SuccessIconWhite />}
              <Typography variant="h1" sx={{ marginTop: '0.5rem' }}>
                {t('screens.verification.verificationComplete.cardTitle')}
              </Typography>
              <Typography variant="body1">
                {t('screens.verification.verificationComplete.cardDescription')}
              </Typography>
            </Box>
          ) : (
            // Entity is created but KYC isn't finished, client can refresh the status of the application or proceed to Adyen
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                flexGrow: 1,
                textAlign: 'left',
                alignItems: 'start',
                gap: '0.5rem',
              }}
            >
              <Typography variant="body1">
                {t('screens.verification.verificationNotComplete.cardText1')}
              </Typography>
              <Typography variant="body1">
                {t('screens.verification.verificationNotComplete.cardText2')}
              </Typography>
              <ul style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <li>
                  <Typography variant="body1">
                    {t('screens.verification.verificationNotComplete.cardItem1')}
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    {t('screens.verification.verificationNotComplete.cardItem2')}
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    {t('screens.verification.verificationNotComplete.cardItem3')}
                  </Typography>
                </li>
              </ul>
              <Typography variant="body1">
                {t('screens.verification.verificationNotComplete.nextToContinueKycNotStarted')}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  width: '100%',
                  gap: '0.5rem',
                  marginTop: '1.5rem',
                }}
              >
                {isKycStarted && (
                  <Button
                    variant="outlined"
                    disableElevation
                    sx={{
                      margin: '0px',
                      flexGrow: '0',
                      height: '48px',
                    }}
                    onClick={() => {
                      setIsLoadingKycStatus(true);
                      refetchKycStatus();
                    }}
                  >
                    {isLoadingKycStatus ? (
                      <>
                        <CircularProgress color="inherit" size="20px" sx={{ marginRight: '8px' }} />
                        {t('screens.verification.verificationNotComplete.refreshingKycStatus')}
                      </>
                    ) : (
                      t('actions.refresh')
                    )}
                  </Button>
                )}

                <Button
                  variant="contained"
                  disableElevation
                  sx={{
                    margin: '0px',
                    flexGrow: '0',
                    height: '48px',
                  }}
                  onClick={loadAndOpenHOP}
                >
                  {isLoading && !(hopRetries > 1 && loadingHop) ? (
                    <>
                      <CircularProgress color="inherit" size="20px" sx={{ marginRight: '8px' }} />
                      {t('actions.loading')}
                    </>
                  ) : (
                    t('screens.verification.verificationNotComplete.goToAdyen')
                  )}
                </Button>
              </Box>
            </Box>
          ))}
      </Card>

      <ActionsContainer>
        <Button
          variant="outlined"
          sx={{
            margin: '0px',
            flexGrow: '0',
            height: '48px',
          }}
          onClick={goToPreviousStep}
        >
          {t('actions.previous')}
        </Button>

        <Box sx={{ display: 'flex', flexDirection: 'row', gap: '0.5rem' }}>
          {!isKycPassed(kycStatus, businessInformation?.issueBankAccount) &&
            !errorCreatingEntity && (
              <Button
                variant="outlined"
                sx={{
                  margin: '0px',
                  flexGrow: '0',
                  height: '48px',
                }}
                onClick={() => {
                  setSkipStepModalOpen(true);
                }}
              >
                {t('actions.skip')}
              </Button>
            )}
          {(isKycPassed(kycStatus, businessInformation?.issueBankAccount) ||
            errorCreatingEntity) && (
            <Button
              variant="contained"
              sx={{
                margin: '0px',
                flexGrow: '0',
                height: '48px',
              }}
              onClick={goToNextStep}
            >
              {t('actions.next')}
            </Button>
          )}
        </Box>
      </ActionsContainer>
    </>
  );
};

export default Verification;
